import { Select, Text } from "@assetwatch/ui";
import type { AssetId } from "../../../lib/workIds";
import type { AssetTreeNode } from "../../../queries/useAssetTreeQuery";
import { useAssetTreeQuery } from "../../../queries/useAssetTreeQuery";

type AssetSelectorProps = {
  value: number | null;
  onChange: (
    assetId: number | null,
    assetName: string | null,
    assetPublicId: AssetId | null,
  ) => void;
  error?: string;
  required?: boolean;
};

export type AssetOption = {
  label: string;
  value: string;
  name: string;
  code: string;
  hierarchyPath: string;
  assetId?: AssetId;
};

export function flattenAssetOptions(
  nodes: AssetTreeNode[],
  depth = 0,
  ancestorNames: string[] = [],
): AssetOption[] {
  return nodes.flatMap((node) => [
    {
      label: `${"  ".repeat(depth)}${node.name} ${node.code}`,
      value: String(node.assetTreeId),
      name: node.name,
      code: node.code,
      hierarchyPath: [...ancestorNames, node.name].join(" > "),
      assetId: node.assetId,
    },
    ...flattenAssetOptions(node.children, depth + 1, [
      ...ancestorNames,
      node.name,
    ]),
  ]);
}

export function AssetSelector({
  value,
  onChange,
  error,
  required,
}: AssetSelectorProps) {
  const { data: assetTreeData, isPending } = useAssetTreeQuery();

  const options = flattenAssetOptions(assetTreeData?.nodes ?? []);

  const handleChange = (selectedValue: string | null) => {
    if (selectedValue === null) {
      onChange(null, null, null);
    } else {
      const assetId = Number(selectedValue);
      const selected = options.find((opt) => opt.value === selectedValue);
      onChange(assetId, selected?.name ?? null, selected?.assetId ?? null);
    }
  };

  if (isPending) {
    return <Text tone="subtle">Loading assets...</Text>;
  }

  return (
    <Select
      searchable
      label="Asset"
      placeholder="Search assets..."
      options={options}
      value={value ? String(value) : null}
      onChange={handleChange}
      renderValue={(option) => {
        const asset = options.find(
          (candidate) => candidate.value === option?.value,
        );
        return asset?.name ?? option?.label;
      }}
      renderOption={(option) => {
        const asset = options.find(
          (candidate) => candidate.value === option.value,
        );
        return asset?.name ?? option.label;
      }}
      error={error}
      required={required}
    />
  );
}
